import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X } from 'lucide-react';

interface ForceUpdateModalProps {
  mode: 'required' | 'recommended';
  storeUrl: string;
}

const DISMISS_KEY = 'force_update_dismissed';

export function ForceUpdateModal({ mode, storeUrl }: ForceUpdateModalProps) {
  const [dismissed, setDismissed] = useState(() => {
    if (mode === 'required') return false;
    return sessionStorage.getItem(DISMISS_KEY) === 'true';
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  };

  const handleUpdate = () => {
    window.open(storeUrl, '_blank');
  };

  if (mode === 'required') {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/95 backdrop-blur-sm">
        <div className="mx-4 flex max-w-sm flex-col items-center gap-6 rounded-2xl border bg-card p-8 text-center shadow-lg">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <Download className="h-8 w-8 text-destructive" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-foreground">Update Required</h2>
            <p className="text-sm text-muted-foreground">
              A new version of Leka is required to continue. Please update your app to the latest version.
            </p>
          </div>
          <Button onClick={handleUpdate} size="lg" className="w-full">
            <Download className="mr-2 h-4 w-4" />
            Update Now
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="mx-4 flex max-w-sm flex-col items-center gap-6 rounded-2xl border bg-card p-8 text-center shadow-lg">
        <button
          onClick={handleDismiss}
          className="absolute right-4 top-4 rounded-full p-1 text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Download className="h-8 w-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-bold text-foreground">Update Available</h2>
          <p className="text-sm text-muted-foreground">
            A new version of Leka is available with improvements and new features.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2">
          <Button onClick={handleUpdate} size="lg" className="w-full">
            <Download className="mr-2 h-4 w-4" />
            Update Now
          </Button>
          <Button onClick={handleDismiss} variant="ghost" size="lg" className="w-full">
            Later
          </Button>
        </div>
      </div>
    </div>
  );
}
